import api from './api'
import type { Opportunity, OpportunityDetail, OpportunityPreferences } from '@/types'

export const opportunitiesService = {
  async list(status?: string) {
    const params = status ? `?status=${status}` : ''
    const { data } = await api.get(`/opportunities${params}`)
    const result = data.data
    return (Array.isArray(result) ? result : result?.opportunities ?? []) as Opportunity[]
  },

  async get(id: string) {
    const { data } = await api.get(`/opportunities/${id}`)
    return data.data as OpportunityDetail
  },

  async createManual(payload: {
    title: string
    description?: string
    scopeOfWork?: string
    projectAddress?: string
    projectTypes?: string[]
    contactName?: string
    contactEmail?: string
    contactPhone?: string
    estimatedBudget?: number
    dueDate?: string
    lead?: string
    estimatorNotes?: string
  }) {
    const { data } = await api.post('/opportunities', payload)
    return data.data as Opportunity
  },

  async uploadPdf(file: File, title?: string) {
    return new Promise<Opportunity>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = async () => {
        try {
          const { data } = await api.post('/opportunities/upload-pdf', {
            pdfData: reader.result as string,
            fileName: file.name,
            title: title || file.name.replace(/\.pdf$/i, ''),
          })
          resolve(data.data as Opportunity)
        } catch (err) {
          reject(err)
        }
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  },

  async fromUrl(url: string, title?: string) {
    const { data } = await api.post('/opportunities/from-url', { url, title })
    return data.data as Opportunity
  },

  async update(id: string, payload: Record<string, unknown>) {
    const { data } = await api.put(`/opportunities/${id}`, payload)
    return data.data as OpportunityDetail
  },

  async accept(id: string, assignedToUserId: string, notes?: string) {
    const { data } = await api.post(`/opportunities/${id}/accept`, { assignedToUserId, notes })
    return data.data
  },

  async reject(id: string, reason: string) {
    const { data } = await api.post(`/opportunities/${id}/reject`, { reason })
    return data.data as Opportunity
  },

  async analyze(id: string) {
    const { data } = await api.post(`/opportunities/${id}/analyze`)
    return data.data as OpportunityDetail
  },

  async remove(id: string) {
    const { data } = await api.delete(`/opportunities/${id}`)
    return data.data
  },

  async listNotes(id: string) {
    const { data } = await api.get(`/opportunities/${id}/notes`)
    return data.data
  },

  async addNote(id: string, content: string) {
    const { data } = await api.post(`/opportunities/${id}/notes`, { content })
    return data.data
  },

  async getPreferences() {
    const res = await api.get('/opportunity-preferences')
    return res.data.data as OpportunityPreferences
  },

  async updatePreferences(payload: Partial<OpportunityPreferences>) {
    const res = await api.put('/opportunity-preferences', payload)
    return res.data.data as OpportunityPreferences
  },

  async convertToRfp(id: string, assignedToUserId?: string) {
    const res = await api.post(`/opportunities/${id}/convert-to-rfp`, { assignedToUserId })
    return res.data.data
  },

  async assign(id: string, assignedToUserId: string, brief?: string) {
    const res = await api.post(`/opportunities/${id}/assign`, { assignedToUserId, brief })
    return res.data.data
  },

  async startEstimate(id: string) {
    const res = await api.post(`/opportunities/${id}/start-estimate`)
    return res.data.data as { opportunity: OpportunityDetail; estimate: { id: string; title: string } }
  },

  async dismiss(id: string) {
    const res = await api.post(`/opportunities/${id}/dismiss`)
    return res.data.data as Opportunity
  },

  async markReviewed(id: string) {
    const res = await api.post(`/opportunities/${id}/mark-reviewed`)
    return res.data.data as Opportunity
  },
}
